import React, { useEffect, useState } from 'react';
import { Info } from 'lucide-react';
import { Button } from '../../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../ui/dialog';
import { formatClock, formatLocalDate } from '../../../lib/eventWorkspace';

const todayLocal = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};

/**
 * Reschedule the event (D5, D6): a new date (and times), kept guests, optionally the new-date email (the
 * caller opens the mailbox composer for Going + Invited after saving) and optionally "ask Going guests to
 * confirm again" (their RSVP resets to No response with an "Asked to reconfirm" chip). `onSubmit` gets
 * `{date, start_time, end_time, notify, reconfirm}`; the caller saves and reports the server's refusals.
 */
export default function RescheduleEventDialog({ open, onClose, onSubmit, event, busy = false, error = '' }) {
    const [date, setDate] = useState('');
    const [start, setStart] = useState('');
    const [end, setEnd] = useState('');
    const [notify, setNotify] = useState(true);
    const [reconfirm, setReconfirm] = useState(false);
    useEffect(() => {
        if (!open) return;
        setDate(''); setStart(event?.start_time || ''); setEnd(event?.end_time || ''); setNotify(true); setReconfirm(false);
    }, [open, event?.start_time, event?.end_time]);
    const valid = Boolean(date) && date >= todayLocal() && date !== event?.date;
    return (
        <Dialog open={open} onOpenChange={(next) => { if (!next && !busy) onClose?.(); }}>
            <DialogContent data-testid="reschedule-event-dialog" className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>Reschedule the event</DialogTitle>
                    <DialogDescription>{`Now ${formatLocalDate(event?.date)}${event?.start_time ? ` · ${formatClock(event.start_time)}` : ''} · same venue`}</DialogDescription>
                </DialogHeader>
                <p className="flex items-start gap-2 text-sm text-stone-700"><Info className="mt-0.5 h-4 w-4 shrink-0 text-stone-500" aria-hidden="true" />Guests, RSVPs, owners and follow-ups are kept.</p>
                <label className="block text-xs font-medium text-stone-600">New date
                    <input type="date" aria-label="New date" min={todayLocal()} value={date} onChange={(e) => setDate(e.target.value)} className="mt-1 w-full rounded border border-stone-200 px-2 py-2 text-sm" />
                </label>
                <div className="grid grid-cols-2 gap-3">
                    <label className="block text-xs font-medium text-stone-600">Starts
                        <input type="time" aria-label="Start time" value={start} onChange={(e) => setStart(e.target.value)} className="mt-1 w-full rounded border border-stone-200 px-2 py-2 text-sm" />
                    </label>
                    <label className="block text-xs font-medium text-stone-600">Ends
                        <input type="time" aria-label="End time" value={end} onChange={(e) => setEnd(e.target.value)} className="mt-1 w-full rounded border border-stone-200 px-2 py-2 text-sm" />
                    </label>
                </div>
                <label className="flex items-start gap-2 text-sm text-stone-700">
                    <input type="checkbox" aria-label="Email Going and Invited guests about the new date" checked={notify} onChange={(e) => setNotify(e.target.checked)} className="mt-0.5 h-4 w-4 accent-stone-900" />
                    <span>Email Going and Invited guests about the new date<span className="block text-xs text-stone-500">Opens your mailbox composer, ready to send.</span></span>
                </label>
                <label className="flex items-start gap-2 text-sm text-stone-700">
                    <input type="checkbox" aria-label="Ask Going guests to confirm again" checked={reconfirm} onChange={(e) => setReconfirm(e.target.checked)} className="mt-0.5 h-4 w-4 accent-stone-900" />
                    <span>Ask Going guests to confirm again (they move back to Invited)<span className="block text-xs text-stone-500">Their RSVP resets to No response until they say yes again.</span></span>
                </label>
                {error && <p role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{error}</p>}
                <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
                    <Button data-testid="reschedule-event-submit" disabled={busy || !valid} onClick={() => onSubmit({ date, start_time: start || undefined, end_time: end || undefined, notify, reconfirm })}>
                        {busy ? 'Saving…' : 'Reschedule'}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
